import React from 'react';

interface ShareCardData {
  score: number;
  grade: string;
  message: string;
  transactions: number;
  timeframe: string;
  rotation: number;
  gradientColors: [string, string];
  gradeColor: string;
}

interface SocialShareCardsProps {
  className?: string;
  onCardClick?: (cardData: ShareCardData) => void;
  onStartSimulation?: () => void;
  showSimulationButton?: boolean;
}

const SocialShareCards: React.FC<SocialShareCardsProps> = ({
  className = '',
  onCardClick,
  onStartSimulation,
  showSimulationButton = true
}) => {
  // Sample card data with different scores and grades - only 3 cards now
  const cardData: ShareCardData[] = [
    {
      score: 73,
      grade: 'C',
      message: "Not bad, but I could earn $89 more! 💰",
      transactions: 156,
      timeframe: '6 MONTHS',
      rotation: -3,
      gradientColors: ['#a29bfe', '#74b9ff'],
      gradeColor: '#fdcb6e'
    },
    {
      score: 48,
      grade: 'F',
      message: "I got an F! Missing $157 in rewards 😬",
      transactions: 192,
      timeframe: '12 MONTHS',
      rotation: 0,
      gradientColors: ['#6c5ce7', '#74b9ff'],
      gradeColor: '#e17055'
    },
    {
      score: 92,
      grade: 'A',
      message: "Crushing my rewards game! 🏆",
      transactions: 203,
      timeframe: '12 MONTHS',
      rotation: 3,
      gradientColors: ['#00b894', '#74b9ff'],
      gradeColor: '#00b894'
    }
  ];

  const handleCardClick = (card: ShareCardData) => {
    if (onCardClick) {
      onCardClick(card);
    }
  };

  const getGradeEmoji = (grade: string): string => {
    switch (grade) {
      case 'A': return '🏆';
      case 'B': return '💪';
      case 'C': return '🤔';
      case 'D': return '😅';
      case 'F': return '😬';
      default: return '🤷';
    }
  };

  return (
    <div
      className={`social-share-cards ${className}`}
      style={{
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
      }}
    >
      {/* Cards Grid - responsive columns */}
      <div className="social-cards-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '30px',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        {cardData.map((card, index) => (
          <div
            key={index}
            className={`share-card share-card-${index}`}
            onClick={() => handleCardClick(card)}
            style={{
              background: `linear-gradient(135deg, ${card.gradientColors[0]}, ${card.gradientColors[1]})`,
              color: 'white',
              padding: '30px 20px',
              borderRadius: '15px',
              textAlign: 'center',
              transform: `rotate(${card.rotation}deg) scale(0.95)`,
              cursor: onCardClick ? 'pointer' : 'default',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              position: 'relative',
              ...(index === 1 && {
                transform: `rotate(${card.rotation}deg) scale(1)`,
                zIndex: 2,
                boxShadow: '0 20px 60px rgba(116, 185, 255, 0.3)'
              })
            }}
            onMouseEnter={(e) => {
              if (onCardClick) {
                e.currentTarget.style.transform = `rotate(0deg) scale(1.05)`;
                e.currentTarget.style.zIndex = '10';
              }
            }}
            onMouseLeave={(e) => {
              if (onCardClick) {
                e.currentTarget.style.transform = index === 1
                  ? `rotate(${card.rotation}deg) scale(1)`
                  : `rotate(${card.rotation}deg) scale(0.95)`;
                e.currentTarget.style.zIndex = index === 1 ? '2' : '1';
              }
            }}
          >
            <h3 style={{
              fontSize: '18px',
              marginBottom: '20px',
              fontWeight: '600',
              opacity: '0.95'
            }}>
              My Stashr.ai Credit Card Optimization Score
            </h3>

            <div style={{
              fontSize: '42px',
              fontWeight: '700',
              marginBottom: '5px',
              lineHeight: '1'
            }}>
              {card.score}<small style={{ fontSize: '20px', opacity: '0.8' }}>/100</small>
            </div>

            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              color: card.gradeColor,
              margin: '8px 0',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              {card.grade}
            </div>

            <div style={{
              fontSize: '15px',
              margin: '15px 0',
              opacity: '0.9',
              lineHeight: '1.4',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              "{card.message}"
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '15px',
              margin: '20px 0',
              padding: '15px 0',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              borderBottom: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  lineHeight: '1'
                }}>
                  {card.transactions}
                </div>
                <div style={{
                  fontSize: '10px',
                  opacity: '0.8',
                  marginTop: '2px',
                  letterSpacing: '0.5px'
                }}>
                  TRANSACTIONS
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  lineHeight: '1'
                }}>
                  {card.timeframe}
                </div>
                <div style={{
                  fontSize: '10px',
                  opacity: '0.8',
                  marginTop: '2px',
                  letterSpacing: '0.5px'
                }}>
                  ANALYZED
                </div>
              </div>
            </div>

            <p style={{
              fontSize: '12px',
              opacity: '0.8',
              margin: '15px 0 5px',
              fontStyle: 'italic'
            }}>
              Can you beat my score? {getGradeEmoji(card.grade)}
            </p>

            <p style={{
              fontSize: '11px',
              opacity: '0.7',
              fontWeight: '500'
            }}>
              Try Stashr.ai
            </p>

            {/* Subtle brand watermark */}
            <div style={{
              position: 'absolute',
              bottom: '8px',
              right: '12px',
              fontSize: '8px',
              opacity: '0.4',
              fontWeight: '500'
            }}>
              stashr.ai
            </div>
          </div>
        ))}
      </div>

      {/* Centered Start Simulation Button */}
      {showSimulationButton && (
        <div style={{
          textAlign: 'center',
          padding: '20px 0'
        }}>
          <button
            className="simulation-button"
            onClick={onStartSimulation}
            style={{
              background: 'linear-gradient(135deg, #00b894, #74b9ff)',
              color: 'white',
              padding: '18px 40px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 25px rgba(0, 184, 148, 0.4)',
              minWidth: '200px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 184, 148, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 184, 148, 0.4)';
            }}
          >
            Start My Simulation
          </button>
          <p style={{
            fontSize: '14px',
            color: '#636e72',
            margin: '15px 0 0',
            opacity: '0.8'
          }}>
            Free analysis • Takes 2 minutes • No credit check
          </p>
        </div>
      )}
    </div>
  );
};

// Individual card component for reuse
interface IndividualShareCardProps {
  score: number;
  grade?: string;
  message?: string;
  transactions?: number;
  timeframe?: string;
  rotation?: number;
  gradientColors?: [string, string];
  gradeColor?: string;
  className?: string;
  onClick?: () => void;
  featured?: boolean;
}

export const IndividualShareCard: React.FC<IndividualShareCardProps> = ({
  score,
  grade,
  message,
  transactions = 192,
  timeframe = '12 MONTHS',
  rotation = 0,
  gradientColors = ['#a29bfe', '#74b9ff'],
  gradeColor = '#e17055',
  className = '',
  onClick,
  featured = false
}) => {
  // Auto-calculate grade if not provided
  const calculatedGrade = grade || (() => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  })();

  // Auto-generate message if not provided
  const calculatedMessage = message || (() => {
    switch (calculatedGrade) {
      case 'A': return "Crushing my rewards game! 🏆";
      case 'B': return "Pretty good at this rewards game! 💪";
      case 'C': return "Not bad, but could earn more! 🤔";
      case 'D': return "Found some money I was missing! 😅";
      case 'F': return `I got an F on my credit card optimization! 😬`;
      default: return "Check out my credit card score!";
    }
  })();

  const getGradeEmoji = (gradeValue: string): string => {
    switch (gradeValue) {
      case 'A': return '🏆';
      case 'B': return '💪';
      case 'C': return '🤔';
      case 'D': return '😅';
      case 'F': return '😬';
      default: return '🤷';
    }
  };

  return (
    <div
      className={`individual-share-card ${className}`}
      onClick={onClick}
      style={{
        background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`,
        color: 'white',
        padding: '30px 20px',
        borderRadius: '15px',
        textAlign: 'center',
        transform: `rotate(${rotation}deg) scale(${featured ? 1 : 0.95})`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        boxShadow: featured
          ? '0 20px 60px rgba(116, 185, 255, 0.3)'
          : '0 10px 30px rgba(0, 0, 0, 0.15)',
        position: 'relative',
        zIndex: featured ? 2 : 1,
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
        maxWidth: '320px',
        margin: '0 auto'
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = `rotate(0deg) scale(1.05)`;
          e.currentTarget.style.zIndex = '10';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = `rotate(${rotation}deg) scale(${featured ? 1 : 0.95})`;
          e.currentTarget.style.zIndex = featured ? '2' : '1';
        }
      }}
    >
      <h3 style={{
        fontSize: '18px',
        marginBottom: '20px',
        fontWeight: '600',
        opacity: '0.95'
      }}>
        My Stashr.ai Credit Card Optimization Score
      </h3>

      <div style={{
        fontSize: '42px',
        fontWeight: '700',
        marginBottom: '5px',
        lineHeight: '1'
      }}>
        {score}<small style={{ fontSize: '20px', opacity: '0.8' }}>/100</small>
      </div>

      <div style={{
        fontSize: '36px',
        fontWeight: '700',
        color: gradeColor,
        margin: '8px 0',
        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }}>
        {calculatedGrade}
      </div>

      <div style={{
        fontSize: '15px',
        margin: '15px 0',
        opacity: '0.9',
        lineHeight: '1.4',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        "{calculatedMessage}"
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '15px',
        margin: '20px 0',
        padding: '15px 0',
        borderTop: '1px solid rgba(255,255,255,0.2)',
        borderBottom: '1px solid rgba(255,255,255,0.2)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            lineHeight: '1'
          }}>
            {transactions}
          </div>
          <div style={{
            fontSize: '10px',
            opacity: '0.8',
            marginTop: '2px',
            letterSpacing: '0.5px'
          }}>
            TRANSACTIONS
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: '1'
          }}>
            {timeframe}
          </div>
          <div style={{
            fontSize: '10px',
            opacity: '0.8',
            marginTop: '2px',
            letterSpacing: '0.5px'
          }}>
            ANALYZED
          </div>
        </div>
      </div>

      <p style={{
        fontSize: '12px',
        opacity: '0.8',
        margin: '15px 0 5px',
        fontStyle: 'italic'
      }}>
        Can you beat my score? {getGradeEmoji(calculatedGrade)}
      </p>

      <p style={{
        fontSize: '11px',
        opacity: '0.7',
        fontWeight: '500'
      }}>
        Try Stashr.ai
      </p>

      {/* Subtle brand watermark */}
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '12px',
        fontSize: '8px',
        opacity: '0.4',
        fontWeight: '500'
      }}>
        stashr.ai
      </div>
    </div>
  );
};

export default SocialShareCards;
